<template>
	<div class="search-picker">
		<!-- Избранное — только в flat-режиме, только когда нет поискового запроса -->
		<template v-if="favoritesKey && !query && favorites.length > 0">
			<div class="section-label">Избранное</div>
			<button
				v-for="item in favorites"
				:key="item.label"
				class="item"
				type="button"
				@click="onPick(item)"
			>
				<VSvgIcon v-if="item.icon" :name="item.icon" class="nav-icon" />
				<span>{{ item.label }}</span>
				<VIcon
					material="star"
					class="star on"
					@click.stop="toggleFavorite(item)"
				/>
			</button>
			<div class="divider" />
			<div class="section-label">Все элементы</div>
		</template>

		<!-- Grouped-режим -->
		<template v-if="groups.length > 0">
			<template v-if="filteredGroups.length > 0">
				<template v-for="group in filteredGroups" :key="group.label">
					<!--
						Заголовок группы — div (не button), чтобы не ломать существующие стили .group-label.
						Кликабельность добавляется через @click и cursor:pointer когда groupsSelectable=true.
					-->
					<div
						class="group-label"
						:class="{ 'group-label_selectable': groupsSelectable }"
						@click="groupsSelectable && onPickGroup(group)"
					>
						{{ group.label }}
					</div>

					<template v-for="(item, index) in group.items" :key="item.label">
						<div v-if="index > 0" class="hr" />
						<button class="item" type="button" @click="onPick(item)">
							<VSvgIcon v-if="item.icon" :name="item.icon" class="nav-icon" />
							{{ item.label }}
						</button>
					</template>
				</template>
			</template>
			<div v-else class="empty">Ничего не найдено</div>
		</template>

		<!-- Flat-режим -->
		<template v-if="items.length > 0">
			<template v-if="filteredItems.length > 0">
				<template v-for="(item, index) in filteredItems" :key="item.label">
					<div v-if="index > 0" class="hr" />
					<button class="item" type="button" @click="onPick(item)">
						<VSvgIcon v-if="item.icon" :name="item.icon" class="nav-icon" />
						<span>{{ item.label }}</span>
						<VIcon
							v-if="favoritesKey"
							:material="favoriteSet.has(item.label) ? 'star' : 'star_border'"
							class="star"
							:class="{ on: favoriteSet.has(item.label) }"
							@click.stop="toggleFavorite(item)"
						/>
					</button>
				</template>
			</template>
			<div v-else class="empty">Ничего не найдено</div>
		</template>

	</div>
</template>

<style lang="scss" src="./VSearchPicker.scss" />

<script lang="ts">
import VIcon from '@/lib/components/VIcon/VIcon.vue';
import type { NavRoute } from '@/lib/components/AppHeader/AppHeaderService';

export default defineComponent({
	name: 'SearchPicker',

	components: {
		VIcon,
	},

	props: {
		query: {
			type: String,
			required: true,
		},

		// Flat-режим: плоский список маршрутов
		items: {
			type: Array as PropType<NavRoute[]>,
			default: () => [],
		},

		// Grouped-режим: сгруппированный список
		groups: {
			type: Array as PropType<{ label: string; items: NavRoute[] }[]>,
			default: () => [],
		},

		// Ключ localStorage для хранения избранного (null — избранное отключено)
		favoritesKey: {
			type: String,
			default: null,
		},

		// Список избранного по умолчанию (используется если в localStorage ещё нет данных)
		defaultFavorites: {
			type: Array as PropType<NavRoute[]>,
			default: () => [],
		},

		// Grouped-режим: если true — заголовки групп кликабельны, эмитят pick-group
		groupsSelectable: {
			type: Boolean,
			default: false,
		},
	},

	emits: ['pick', 'pick-group'],

	data() {
		return {
			favorites: this.favoritesKey
				? this.loadFavorites(this.favoritesKey, this.defaultFavorites)
				: [] as NavRoute[],
		};
	},

	computed: {
		filteredItems(): NavRoute[] {
			if (!this.query) {
				return this.items;
			}
			const q = this.query.toLowerCase();
			return this.items.filter(item => item.label.toLowerCase().includes(q));
		},

		filteredGroups(): { label: string; items: NavRoute[] }[] {
			if (!this.query) {
				return this.groups;
			}
			const q = this.query.toLowerCase();
			return this.groups
				.map(group => ({
					label: group.label,
					items: group.items.filter(item => item.label.toLowerCase().includes(q)),
				}))
				.filter(group => group.items.length > 0);
		},

		// Ключ для O(1) проверки — по label, т.к. path может дублироваться (Заказы у buyer/seller/admin)
		favoriteSet(): Set<string> {
			return new Set(this.favorites.map(f => f.label));
		},
	},

	methods: {
		/**
		 * Загружает избранное из localStorage по ключу.
		 * Если данных нет или localStorage недоступен (приватный режим) — возвращает копию defaults.
		 */
    loadFavorites(storageKey: string, defaults: NavRoute[]): NavRoute[] {
      let result: NavRoute[];

      try {
        const stored = localStorage.getItem(storageKey);
        if (stored !== null) {
          result = JSON.parse(stored) as NavRoute[];
        } else {
          result = [...defaults];
        }
      } catch {
        result = [...defaults];
      }

      /** сорт по алфавиту при открытии списка */
      return result.sort((a, b) => a.label.localeCompare(b.label, 'ru'));
    },

		/**
		 * Сохраняет избранное в localStorage. Если localStorage недоступен — молча игнорируем.
		 */
		saveFavorites(storageKey: string, favorites: NavRoute[]): void {
			try {
				localStorage.setItem(storageKey, JSON.stringify(favorites));
			} catch {
				// localStorage недоступен — игнорируем
			}
		},

		toggleFavorite(item: NavRoute): void {
			if (!this.favoritesKey) {
				return;
			}
			const index = this.favorites.findIndex(fav => fav.label === item.label);
			if (index > -1) {
				this.favorites.splice(index, 1);
			} else {
				this.favorites.push(item);
        /** сортируем по алфавиту при добавлении нового раздела в избранное  */
        this.favorites.sort((a, b) => a.label.localeCompare(b.label, 'ru'));
			}
			this.saveFavorites(this.favoritesKey, this.favorites);
		},

		onPick(item: NavRoute): void {
			this.$emit('pick', item);
		},

		onPickGroup(group: { label: string; items: NavRoute[] }): void {
			this.$emit('pick-group', group);
		},
	},
});
</script>
